/**
 * The screen shown when there is nothing to play — and the answer to "what is
 * actually happening when it goes white?"
 *
 * Every non-playing state in this app rendered a white background, the Signy
 * logo and one sentence. From across a reception that reads as a blank screen,
 * and the customer reports it as one. Worse, the sentence explaining the most
 * common cause was hidden precisely when it applied: the offline message was
 * gated on `navigator.onLine`, so being genuinely offline produced a logo and
 * no text at all.
 *
 * So this screen now says who it is and what it is doing. A photograph from a
 * customer becomes a diagnosis: which display, which device, which state, and
 * how long it has been there. That is the difference between "screen 20 is
 * white again" and "screen 20 has been waiting for the hub since 06:48".
 *
 * It also reports itself. Sitting in a non-playing state is not an error any
 * single line of code throws, so nothing ever told us it was happening — the
 * production project has been almost silent while screens sat blank. After
 * REPORT_AFTER_MS this reports once, with the state and the display identity,
 * so the pattern becomes visible without anyone standing in front of a panel.
 */

import { useEffect, useRef } from 'react';
import { SignyLogo } from './SignyLogo';
import { reportPlaybackStall, trackEvent } from '@/lib/errorReporting';

// Long enough that ordinary start-up and a slow reconnect are not reported as
// faults. A display that is still here after a minute is stuck, not starting.
const REPORT_AFTER_MS = 60 * 1000;

interface StatusScreenProps {
  /** Short, human sentence in Swedish — what the viewer sees. */
  message: string;
  /** Stable machine-readable state, used for grouping in Sentry. */
  state: string;
  deviceId?: string;
  displayId?: number;
  venueId?: number;
}

function twoDigits(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

export function StatusScreen({ message, state, deviceId, displayId, venueId }: StatusScreenProps) {
  const since = useRef(new Date());

  useEffect(() => {
    since.current = new Date();
    trackEvent('display', 'status screen', { state, displayId, deviceId });

    const timer = setTimeout(() => {
      reportPlaybackStall('display showing a status screen instead of content', {
        state,
        displayId,
        venueId,
        deviceId,
        forMs: REPORT_AFTER_MS,
        online: navigator.onLine,
      });
    }, REPORT_AFTER_MS);

    return () => clearTimeout(timer);
  }, [state, displayId, venueId, deviceId]);

  const at = since.current;
  const clock = `${twoDigits(at.getHours())}:${twoDigits(at.getMinutes())}`;

  // Deliberately terse and deliberately legible from a distance: this line
  // exists to survive being photographed on a phone from across a room.
  const identity = [
    displayId !== undefined ? `skärm ${displayId}` : null,
    deviceId ? `enhet ${deviceId.slice(0, 8)}` : null,
    state,
    `sedan ${clock}`,
    navigator.onLine ? null : 'offline',
  ]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <div className="relative flex h-screen w-full flex-col items-center justify-center space-y-8 bg-white">
      <div className="w-[300px]">
        <SignyLogo />
      </div>

      <p className="w-1/2 text-center text-lg font-semibold">{message}</p>

      <div className="absolute bottom-0 left-0 right-0 px-6 py-4 text-center font-mono text-base text-neutral-500">
        {identity}
      </div>
    </div>
  );
}

export default StatusScreen;
